import { useState, useEffect, useRef, useCallback } from 'react'
import type { UUID } from 'crypto'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useToken } from '../hooks/useToken'
import { useGenerateProfile } from '../hooks/useGenerateProfile'
import { useAppendDescriptionAndGenerate } from '../hooks/useAppendDescriptionAndGenerate'
import ProgressScreen from '../components/ProgressScreen'
import AssessmentGate from '../components/AssessmentGate'
import type { ProfileAssessment } from '@/models/Orgs'
import { useSSEProgress } from '../hooks/useSSEProgress'

const HELP_TEXTS = [
  'We\'re analysing your documents, website, and other information to create a comprehensive profile of your organisation. This usually takes a minute or two.',
]

const PROGRESS_INTERVAL_MS = 1000
const PROGRESS_DURATION_MS = 30000
const PROGRESS_INCREMENT = 90 / (PROGRESS_DURATION_MS / PROGRESS_INTERVAL_MS)

export default function ProfileGenerating() {
  const {
    orgUuid, goNext, goBack, setHideNav,
    staleFrom, setStaleFrom, profileGenerated, setProfileGenerated,
  } = useOnboardingV2()
  const [wizardData] = useWizardData()
  const { getToken } = useToken()

  const generateProfile = useGenerateProfile()
  const appendAndGenerate = useAppendDescriptionAndGenerate()

  // Refs for mutation functions to avoid stale closures
  const generateProfileRef = useRef(generateProfile)
  generateProfileRef.current = generateProfile
  const appendAndGenerateRef = useRef(appendAndGenerate)
  appendAndGenerateRef.current = appendAndGenerate
  const staleFromRef = useRef(staleFrom)
  staleFromRef.current = staleFrom

  // Abort guard — prevents post-unmount goNext/persist calls
  const unmountedRef = useRef(false)
  useEffect(() => () => { unmountedRef.current = true }, [])

  // Fake progress
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [profileJobId, setProfileJobId] = useState<string | null>(null)
  const [profileJobToken, setProfileJobToken] = useState<string | null>(null)
  const startedRef = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Gate state
  const [assessment, setAssessment] = useState<ProfileAssessment | null>(null)
  const [showGate, setShowGate] = useState(false)
  const [gateInputs, setGateInputs] = useState<Record<number, string>>({})
  const contextAppendedRef = useRef(false)

  const startProgress = useCallback(() => {
    setProgress(0)
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          return prev
        }
        return prev + PROGRESS_INCREMENT
      })
    }, PROGRESS_INTERVAL_MS)
  }, [])

  const stopProgress = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
  }, [])

  const completeProfileGeneration = useCallback((result: any) => {
    if (unmountedRef.current) return
    stopProgress()
    if (result?.profile) {
      setProfileGenerated(true)
      if (staleFromRef.current !== null && staleFromRef.current <= STEPS.PROFILE_GENERATING) {
        setStaleFrom(STEPS.SCAN_RUNNING)
      }
      setProgress(100)
      goNext()
    } else if (result?.assessment) {
      // Still bad quality — show gate again with updated suggestions
      setAssessment(result.assessment)
      setShowGate(true)
      setGateInputs({})
    }
  }, [goNext, setProfileGenerated, setStaleFrom, stopProgress])

  const profileProgress = useSSEProgress({
    type: 'profile',
    jobId: profileJobId,
    token: profileJobToken,
    onError: () => {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    },
  })

  useEffect(() => {
    if (profileProgress.status !== 'completed') return
    completeProfileGeneration(profileProgress.data)
    setProfileJobId(null)
  }, [profileProgress.status, profileProgress.data, completeProfileGeneration])

  const startProfileJob = useCallback(async (t: string, additionalText?: string) => {
    const result = additionalText
      ? await appendAndGenerateRef.current.mutateAsync({
          token: t,
          orgUuid: orgUuid as UUID,
          additionalText,
        })
      : await generateProfileRef.current.mutateAsync({
          token: t,
          orgUuid: orgUuid as UUID,
        })
    setProfileJobToken(t)
    setProfileJobId(result.job_id)
  }, [orgUuid])

  const runGeneration = useCallback(async () => {
    try {
      const t = await getToken()

      // Append additional context and generate in one call (only once)
      if (wizardData.additionalContext && orgUuid && !contextAppendedRef.current) {
        contextAppendedRef.current = true
        await startProfileJob(t, wizardData.additionalContext)
        return
      }

      await startProfileJob(t)
    } catch {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    }
  }, [getToken, wizardData.additionalContext, orgUuid, startProfileJob, stopProgress])

  // Fire once on mount
  useEffect(() => {
    if (startedRef.current || !orgUuid) return
    startedRef.current = true
    startProgress()
    runGeneration()

    return () => { stopProgress() }
  }, [orgUuid])

  // Hide shell nav during progress, show during gate
  useEffect(() => {
    setHideNav(!showGate)
    return () => setHideNav(false)
  }, [showGate, setHideNav])

  // Submit the gate answers and re-generate. AssessmentGate drives the shell's
  // single forward button and calls this once the user advances past the last
  // question; we own the resulting navigation. Generation runs through the
  // async job queue, so completion is handled by the SSE progress effect.
  const handleGateSubmit = useCallback(async (combinedText: string) => {
    // Reset to progress mode
    setShowGate(false)
    setAssessment(null)

    try {
      const t = await getToken()
      startProgress()

      await startProfileJob(t, combinedText)
    } catch {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    }
  }, [getToken, startProgress, stopProgress, startProfileJob])

  // Error retry (network/server errors, not quality retries)
  const handleRetry = useCallback(() => {
    setError(null)
    setProgress(0)
    startedRef.current = false
    startProgress()
    runGeneration()
  }, [startProgress, runGeneration])

  // Gate mode — render assessment form (shell renders nav buttons)
  if (showGate && assessment) {
    return (
      <AssessmentGate
        heading="We need a bit more information"
        items={assessment.Suggestions.slice(0, 3).map(s => ({ label: s }))}
        quality={assessment.Quality}
        inputs={gateInputs}
        onInputChange={(i, v) => setGateInputs(prev => ({ ...prev, [i]: v }))}
        onSubmit={handleGateSubmit}
      />
    )
  }

  // Progress mode
  return (
    <ProgressScreen
      statusText="Generating your organisation profile..."
      helpTexts={HELP_TEXTS}
      progress={profileProgress.progress || progress}
      error={error}
      onRetry={handleRetry}
      onBack={() => goBack()}
    />
  )
}
